import { supabase } from "@/integrations/supabase/client";
import { PeopleRole } from "@/types";

export const fetchPeopleRoles = async (): Promise<PeopleRole[]> => {
  const { data, error } = await supabase
    .from("people_roles")
    .select("*")
    .order("id");

  if (error) {
    console.error("Error fetching people roles:", error);
    throw error;
  }

  // Convert from Supabase format to our app format
  return data.map((role) => ({
    id: role.id,
    name: role.name,
    description: role.description,
    isActive: role.is_active,
    createdAt: role.created_at,
    updatedAt: role.updated_at,
  }));
};

export const fetchPeopleRole = async (id: number): Promise<PeopleRole> => {
  const { data, error } = await supabase
    .from("people_roles")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    console.error("Error fetching people role:", error);
    throw error;
  }

  // Convert from Supabase format to our app format
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    isActive: data.is_active,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
};

export const createPeopleRole = async (
  name: string,
  description?: string
): Promise<PeopleRole> => {
  const { data, error } = await supabase
    .from("people_roles")
    .insert({
      name,
      description,
      is_active: true,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating people role:", error);
    throw error;
  }

  // Convert from Supabase format to our app format
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    isActive: data.is_active,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
};

export const updatePeopleRole = async (
  id: number,
  updates: Partial<PeopleRole>
): Promise<PeopleRole> => {
  // Convert from our format to Supabase format
  type SupabaseRoleUpdates = {
    name?: string;
    description?: string | null;
    is_active?: boolean;
  };

  const supabaseUpdates: SupabaseRoleUpdates = {};
  if (updates.name !== undefined) supabaseUpdates.name = updates.name;
  if (updates.description !== undefined)
    supabaseUpdates.description = updates.description;
  if (updates.isActive !== undefined)
    supabaseUpdates.is_active = updates.isActive;

  const { data, error } = await supabase
    .from("people_roles")
    .update(supabaseUpdates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating people role:", error);
    throw error;
  }

  // Convert from Supabase format to our app format
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    isActive: data.is_active,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
};

export const deletePeopleRole = async (id: number): Promise<void> => {
  const { error } = await supabase.from("people_roles").delete().eq("id", id);

  if (error) {
    console.error("Error deleting people role:", error);
    throw error;
  }
};

// Get all roles with their people counts in a single query
export const getAllRolesWithCounts = async (): Promise<PeopleRole[]> => {
  // Query that joins people_roles with people_to_roles to count people per role
  const { data, error } = await supabase.from("people_roles").select(`
      *,
      people_count:people_to_roles(count)
    `);

  if (error) {
    console.error("Error fetching roles with counts:", error);
    throw error;
  }

  // Transform the results to include the count
  return data.map((role) => {
    // Get the people count - safely handle undefined or null
    const peopleCount =
      Array.isArray(role.people_count) && role.people_count.length > 0
        ? role.people_count[0]?.count || 0
        : 0;

    return {
      id: role.id,
      name: role.name,
      description: role.description,
      isActive: role.is_active,
      createdAt: role.created_at,
      updatedAt: role.updated_at,
      peopleCount: peopleCount, // Include the count directly in the role object
    };
  });
};
